import { useMemo } from "react";
import { useAppSelector } from "@store/index";
import { chartConfigSelector } from "@store/slices/chart-config-slice";
import { getTimePeriodTitleSuffix } from "@utils/time-period-display";
import { getRollingAverageTitleSuffix } from "@utils/rolling-average-utils";
import { getPointInTimeUnitForPeriod } from "@utils/time-period-utils";
import {
  useGetDatasetQuestionVariablesByElementQuery,
  useGetDatasetQuestionsQuery,
} from "@store/api/dataset-api";
import {
  buildHiddenReasonMap,
  buildHiddenLabelReasonMap,
} from "@utils/chart-visibility-utils";

export function useBarChartDerivedState(
  isRefetchingData: boolean,
  dataAvailabilityDate?: string | null,
) {
  const { currentConfig, datasetId, questionId } = useAppSelector(chartConfigSelector);
  const questions = useAppSelector((state) => state.chartConfig.question);

  // Fetched for every bar chart (not just grid/focus ones) so BarCustomizationSection
  // can tag "Hidden in dataset" on bars sourced from a plain Single/Multiple question too.
  const { data: questionVariablesData } = useGetDatasetQuestionVariablesByElementQuery(
    { id: datasetId!, dataset_element_id: questionId! },
    { skip: !datasetId || !questionId },
  );

  // By-id lookup (returns the question even when hidden, unlike the visibility-
  // filtered list) so the drawer's question summary can surface a now-hidden
  // source question — the question stays fixed in the editor regardless.
  const { data: questionData } = useGetDatasetQuestionsQuery(
    { id: datasetId!, dataset_element_ids: [questionId!], page_size: 1 },
    { skip: !datasetId || !questionId },
  );

  const isQuestionHidden = !!questionData?.questions?.find(
    (q) => q.datasetElementId === questionId,
  )?.hidden;

  const allVariables = useMemo(
    () => questionVariablesData?.variables || [],
    [questionVariablesData],
  );

  const allValues = useMemo(
    () => questionVariablesData?.values || [],
    [questionVariablesData],
  );

  // datasetElementId -> "Hidden in dataset", combining both variables and
  // values so any rollup-source lookup (which already carries real
  // datasetElementIds) only needs one map.
  const hiddenSourceReasons = useMemo(() => {
    const reasons = buildHiddenReasonMap(allVariables, (v) => v.datasetElementId);
    buildHiddenReasonMap(allValues, (v) => v.datasetElementId).forEach(
      (reason, id) => reasons.set(id, reason),
    );
    return reasons;
  }, [allVariables, allValues]);

  const hiddenLabelReasons = useMemo(() => {
    const reasons = buildHiddenLabelReasonMap(allVariables);
    buildHiddenLabelReasonMap(allValues).forEach((reason, label) =>
      reasons.set(label, reason),
    );
    return reasons;
  }, [allVariables, allValues]);

  const isAvailableData =
    !currentConfig.series ||
    currentConfig.series.length === 0 ||
    currentConfig.series.every((s) => s.data.length === 0);

  const canSave = !isAvailableData && !isRefetchingData;

  const hasSampleSizeBelowThreshold =
    !isRefetchingData &&
    currentConfig.statisticalTesting?.enabled &&
    currentConfig.series &&
    currentConfig.series.length > 0 &&
    currentConfig.series.some(
      (series) =>
        (series as any).baseN !== undefined &&
        (series as any).baseN !== null &&
        (series as any).baseN <
          currentConfig.statisticalTesting.minimumSampleSize,
    );

  // Check if rolling average has partial historical data (less than requested window)
  // This happens when: rollingAverage is enabled AND effectiveRollingPeriodCount is less than windowSize
  const hasPartialHistoricalData =
    currentConfig.rollingAverage?.enabled &&
    currentConfig.statisticalTesting?.enabled &&
    (currentConfig as any).effectiveRollingPeriodCount !== undefined &&
    currentConfig.rollingAverage?.windowSize !== undefined &&
    (currentConfig as any).effectiveRollingPeriodCount > 0 &&
    (currentConfig as any).effectiveRollingPeriodCount <
      currentConfig.rollingAverage.windowSize;

  // Check if statistical testing is enabled but no previous period/rolling window data exists
  // Bar chart series have statSignificance property when stat testing data is available
  const hasNoPreviousPeriodData =
    !isRefetchingData &&
    currentConfig.statisticalTesting?.enabled &&
    currentConfig.series &&
    currentConfig.series.length > 0 &&
    currentConfig.series.every(
      (series) =>
        (series as any).statSignificance === undefined ||
        (series as any).statSignificance === null,
    );

  const isAllTimePeriod =
    currentConfig.timePeriodSelection?.timePeriod === "all_time";

  const headerTitle = useMemo(() => {
    if (!questions?.name) return "Configure your bar chart";

    let baseTitle = questions.name;

    const hasCustomTitle =
      currentConfig.title !== "" &&
      currentConfig.title !== "Brand Tracking" &&
      questions &&
      currentConfig.title !== questions.name;

    // If title is customized, show the custom title
    if (hasCustomTitle) {
      return currentConfig.title;
    }

    if (currentConfig.focusEntityDisplayLabel) {
      baseTitle = `${baseTitle}  (${currentConfig.focusEntityDisplayLabel})`;
    }

    const rollingSuffix =
      currentConfig.rollingAverage?.enabled && currentConfig.rollingAverage?.windowSize
        ? getRollingAverageTitleSuffix(currentConfig.rollingAverage.windowSize)
        : "";

    if (currentConfig.timePeriodSelection) {
      const tp = currentConfig.timePeriodSelection.timePeriod;
      // For named periods, skip stale saved dates and re-resolve from current
      // dataset availability so the drawer title stays in sync with the layout.
      const isNamedPeriod = getPointInTimeUnitForPeriod(tp) !== null;
      const timePeriodSuffix = getTimePeriodTitleSuffix(
        tp,
        isNamedPeriod ? null : currentConfig.timePeriodSelection.startDate,
        isNamedPeriod ? null : currentConfig.timePeriodSelection.endDate,
        dataAvailabilityDate ?? undefined,
      );
      if (timePeriodSuffix)
        return `${baseTitle}${timePeriodSuffix}${rollingSuffix}`;
    }

    return `${baseTitle}${rollingSuffix}`;
  }, [
    questions,
    currentConfig.title,
    currentConfig.focusEntityDisplayLabel,
    currentConfig.statisticalTesting?.enabled,
    currentConfig.timePeriodSelection,
    currentConfig.rollingAverage,
    currentConfig.effectiveRollingPeriodCount,
    dataAvailabilityDate,
  ]);

  return {
    questions,
    allVariables,
    allValues,
    hiddenSourceReasons,
    hiddenLabelReasons,
    isQuestionHidden,
    isAvailableData,
    canSave,
    hasSampleSizeBelowThreshold,
    hasPartialHistoricalData,
    hasNoPreviousPeriodData,
    headerTitle,
    isAllTimePeriod,
  };
}
